import { Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { FcDocument } from "react-icons/fc";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../../components/ButtonComponent";
import InputComponent from "../../../components/InputComponent";
import TableComponent, { OperationsCell, } from "../../../components/TableComponent";
import { useAvailableModulesContext } from "../../../context/AvailableModulesContext";
import Toast from "../../../helpers/Toast";
import { Utilities } from "../../../helpers/Utilities";
import { EquipmentDTO } from "../../../models/Equipment/EquipmentDTO";
import { Parameter } from "../../../models/Parameter";
import { EquipmentService } from "../../../services/EquipmentService";
import { SelectItemsProps } from "../../SearchSelectComponent";
import SearchOrTypeSelectComponent from "../../SearchOrTypeSelectComponent";
import { EquipmentUtilities } from "../../../EquipmentUtilities";
import SelectComponent from "../../SelectComponent";

export default function EquipmentIndexA() {
  const intl = useIntl();
  //Declaração de outros estados e variáveis importantes
  const navigate = useNavigate();
  const equipmentService = new EquipmentService();
  const loanEquipmentOptions = [
    { label: intl.formatMessage({ id: "Common.Text.Yes" }), value: "true" },
    { label: intl.formatMessage({ id: "Common.Text.No" }), value: "false" }
  ];

  const pageSizes: number[] = [5, 15, 30];
  const [data, setData] = useState<EquipmentDTO[]>([]);
  const [isFetchingEquipmentsLoading, setIsFetchingEquipmentsLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [searchParams] = useSearchParams();

  //Vamos tentar obter a page e a pageSize pelos query parameters
  //Usamos useState em vez de simple var, para não estar sempre a obter o valor a cada re-render, desta forma o page e o pageSize são sempre o mesmo
  //porque nunca estamos a fazer setState à var
  const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
  const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));

  //Search Parameters
  const [name, setName] = useState<string>(Utilities.LoadParameterFromURLQuery("Name", "string", ""));
  const [serialNumber, setSerialNumber] = useState<string>(Utilities.LoadParameterFromURLQuery("SerialNumber", "string", ""));
  const [brand, setBrand] = useState<string>(Utilities.LoadParameterFromURLQuery("Brand", "string", ""));
  const [model, setModel] = useState<string>(Utilities.LoadParameterFromURLQuery("Model", "string", ""));
  const [codClient, setCodClient] = useState<string>(Utilities.LoadParameterFromURLQuery("CodClient", "string", ""));
  const [clientName, setClientName] = useState<string>(Utilities.LoadParameterFromURLQuery("ClientName", "string", ""));
  const [carPlate, setCarPlate] = useState<string>(Utilities.LoadParameterFromURLQuery("CarPlate", "string", ""));
  const [sku, setSku] = useState<string>(Utilities.LoadParameterFromURLQuery("Sku", "string", ""));
  const [isLoanEquipment, setIsLoanEquipment] = useState<string>(Utilities.LoadParameterFromURLQuery("IsLoanEquipment", "string", ""));

  const [currentPage, setCurrentPage] = useState<number>();
  const [currentPageSize, setCurrentPageSize] = useState<number>();
  const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
  const [goToPage, setGoToPage] = useState<number>();
  const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
  const [reloadSearch, setReloadSearch] = useState<boolean>(false);

  const { availableModules } = useAvailableModulesContext();
  const [isActiveMaintenanceModule, setIsActiveMaintenanceModule] = useState<boolean>(false);
  const [isActiveCarPlate, setIsActiveCarPlate] = useState<boolean>(false);
  const [isActiveLoanEquipmentModule, setIsActiveLoanEquipmentModule] = useState<boolean>(false);
  const [selectedBrand, setSelectedBrand] = useState<SelectItemsProps | null>(brand ? { label: brand, value: brand } : null);
  const [selectedModel, setSelectedModel] = useState<SelectItemsProps | null>(model ? { label: model, value: model } : null);

  useEffect(() => {
    if (availableModules.includes("IsActiveMaintenanceModule")) {
      setIsActiveMaintenanceModule(true);
    }
    if (availableModules.includes("IsActiveCarPlate")) {
      setIsActiveCarPlate(true);
    }
    if (availableModules.includes("IsActiveLoanEquipmentModule")) {
      setIsActiveLoanEquipmentModule(true);
    }
  }, [availableModules]);

  const downloadDataSheet = async (id: number) => {
    setIsFetchingEquipmentsLoading(true);
    var response = await equipmentService.DownloadDataSheet(id);
    if (response.success === false) {
      Toast.Show("error", response.message);
      setIsFetchingEquipmentsLoading(false);
      return;
    }

    Toast.Show("success", response.message);
    setIsFetchingEquipmentsLoading(false);
  }

  const columns = React.useMemo(
    () => {
      const cols = [
        {
          Header: "",
          accessor: " ",
          Cell: (props: any) => {
            return (
              <div style={{ display: "flex" }}>
                {OperationsCell(props.cell.row.original.erpId, "equipment")}
                {isActiveMaintenanceModule && props.cell.row.original.hasDataSheet && (
                  <Tooltip title="Ficha Técnica" aria-label="Ficha Técnica" enterNextDelay={500} enterDelay={500}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        alignContent: "center",
                        flexDirection: "column",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        downloadDataSheet(props.cell.row.original.id);
                      }}
                    >
                      <FcDocument className="icons" />
                    </div>
                  </Tooltip>
                )}
              </div>
            );
          },
          width: 5,
        },
        {
          Header: <FormattedMessage id="Screens.Equipments.Table.EquipmentName" />,
          accessor: "name",
          width: 20,
        },
        ...(isActiveCarPlate ? [{
          Header: <FormattedMessage id="Screens.Equipments.Table.CarPlate" />,
          accessor: "carPlate",
          width: 15,
        }] : []),
        {
          Header: <FormattedMessage id="Screens.Equipments.Table.NumSerie" />,
          accessor: "serialNumber",
          width: 15,
        },
        {
          Header: <FormattedMessage id="Screens.Equipments.Table.Brand" />,
          accessor: "brand",
          width: 10,
        },
        {
          Header: <FormattedMessage id="Screens.Equipments.Table.Model" />,
          accessor: "model",
          width: 10,
        },
        {
          Header: <FormattedMessage id="Screens.Equipments.Table.Sku" />,
          accessor: "sku",
          width: 5,
        },
        ...(isActiveLoanEquipmentModule ? [{
          Header: <FormattedMessage id="Screens.Equipment.Table.IsLoanEquipment" />,
          accessor: "isLoanEquipment",
          width: 10,
          Cell: (props: any) => {
            return (
              <div>
                {props.cell.value === true ? intl.formatMessage({ id: "Common.Text.Yes" }) : intl.formatMessage({ id: "Common.Text.No" })}
              </div>
            );
          },
        }] : []),
        {
          Header: <FormattedMessage id="Screens.Equipments.Table.ClientCode" />,
          accessor: "client.erpId",
          width: 10,
        },
        {
          Header: <FormattedMessage id="Screens.Equipments.Table.ClientName" />,
          accessor: "client.name",
          width: 30,
        },
      ];

      return cols;
    },
    [isActiveMaintenanceModule, isActiveCarPlate, isActiveLoanEquipmentModule]
  );

  const fetchData = async (
    page: number,
    pageSize: number,
    sortBy: SortingRule<object>[] | null
  ) => {

    //Atualizamos na history do browser a current page e pageSize
    var searchUrl: string = [
      `?page=${page}&pageSize=${pageSize}`,
      ...[
        ["name", "string", name],
        ["serialNumber", "string", serialNumber],
        ["brand", "string", brand],
        ["model", "string", model],
        ["codClient", "string", codClient],
        ["clientName", "string", clientName],
        ["carPlate", "string", carPlate],
        ["sku", "string", sku],
        ["isLoanEquipment", "string", isLoanEquipment],
      ].map(([key, type, value]) => value ? `${key}=${value}&` : '')
    ].join('');

    // Ordenação
    if (sortBy != null && sortBy.length > 0) {
      searchUrl += `${Utilities.AddParamToQueryIfValue(
        "sortField",
        "string",
        sortBy[0].id
      )}${Utilities.AddParamToQueryIfValue(
        "sortOrder",
        "string",
        sortBy[0].desc === true ? "DESC" : "ASC"
      )}`;
    }

    //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota
    if (searchParams.toString() === "") {
      navigate(`/equipments${searchUrl}`, { replace: true });
    } else {
      //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
      navigate(`/equipments${searchUrl}`);
    }

    setIsFetchingEquipmentsLoading(true);

    //Vamos obter os dados
    var response = await equipmentService.GetAll(
      mountSearchParameters(),
      Utilities.MountSortParameters(sortBy),
      page,
      pageSize
    );

    if (response.success !== true) {
      setData([]);
      setPageCount(0);
      setIsFetchingEquipmentsLoading(false);
      setCurrentPageSize(0);
      setCurrentPage(0);
      setCurrentSorting(null);
      Toast.Show("error", response.message);
      return;
    }

    //Atribuímos os dados
    setData(response.items);
    setPageCount(response.totalPages);
    setIsFetchingEquipmentsLoading(false);
    setCurrentPageSize(pageSize);
    setCurrentPage(page);
    setCurrentSorting(sortBy);
    setGoToPage(0);

    //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
    if (response.totalPages > 0 && page > response.totalPages) {
      setGoToPage(1);
    }
  };

  //Método para montar os parâmetros de pesquisa
  const mountSearchParameters = (): Parameter[] => {
    var parameters: Parameter[] = [];

    parameters.push(new Parameter("name", name));
    parameters.push(new Parameter("serialNumber", serialNumber));
    parameters.push(new Parameter("brand", brand));
    parameters.push(new Parameter("model", model));
    parameters.push(new Parameter("codClient", codClient));
    parameters.push(new Parameter("clientName", clientName));
    parameters.push(new Parameter("carPlate", carPlate));
    parameters.push(new Parameter("sku", sku));
    parameters.push(new Parameter("isLoanEquipment", isLoanEquipment));

    return parameters;
  };

  const onEnter = () => {
    fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  };
  const onButtonClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);
    fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  };
  const onRemoveFiltersClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);

    setName("");
    setSerialNumber("");
    setBrand("");
    setModel("");
    setClientName("");
    setCodClient("")
    setCarPlate("");
    setSku("");
    setIsLoanEquipment("");
    setSelectedBrand(null);
    setSelectedModel(null);
    setReloadSearch(true);
  };

  useEffect(() => {
    if (reloadSearch === true) {
      fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
      setReloadSearch(false);
    }
    return () => { };
  }, [reloadSearch]);

  return (
    <>
      <Row>
        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={name}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.EquipmentName" })}
          readOnly={isFetchingEquipmentsLoading}
          onChange={(t: string) => setName(t)}
        />

        {(isActiveCarPlate &&
          <InputComponent
            xl={2}
            lg={3}
            md={5}
            sm={12}
            xs={12}
            style={{ width: "100%" }}
            type={"text"}
            onEnter={onEnter}
            value={carPlate}
            label={intl.formatMessage({ id: "Screens.Equipments.Search.CarPlate" })}
            readOnly={isFetchingEquipmentsLoading}
            onChange={(t: string) => setCarPlate(t)}
          />
        )}

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={serialNumber}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.NumSerie" })}
          readOnly={isFetchingEquipmentsLoading}
          onChange={(t: string) => setSerialNumber(t)}
        />

        <SearchOrTypeSelectComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.Brand" })}
          value={selectedBrand}
          loadOptions={EquipmentUtilities.SearchBrand}
          onChange={(selected) => {
            setSelectedBrand(selected);
            setBrand(selected?.value || "");
          }}
          isDisabled={isFetchingEquipmentsLoading}
        />

        <SearchOrTypeSelectComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.Model" })}
          value={selectedModel}
          loadOptions={EquipmentUtilities.SearchModel}
          onChange={(selected) => {
            setSelectedModel(selected);
            setModel(selected?.value || "");
          }}
          isDisabled={isFetchingEquipmentsLoading}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={sku}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.Sku" })}
          readOnly={isFetchingEquipmentsLoading}
          onChange={(t: string) => setSku(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={codClient}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.ClientCode" })}
          readOnly={isFetchingEquipmentsLoading}
          onChange={(t: string) => setCodClient(t)}
        />

        <InputComponent
          xl={2}
          lg={3}
          md={5}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"text"}
          onEnter={onEnter}
          value={clientName}
          label={intl.formatMessage({ id: "Screens.Equipments.Search.ClientName" })}
          readOnly={isFetchingEquipmentsLoading}
          onChange={(t: string) => setClientName(t)}
        />
        {isActiveLoanEquipmentModule &&
          <SelectComponent
            xl={2}
            lg={3}
            md={5}
            sm={12}
            xs={12}
            label={intl.formatMessage({ id: "Screens.Equipment.IsLoanEquipment" })}
            items={loanEquipmentOptions}
            selectedItemValue={isLoanEquipment}
            onChange={(value) => setIsLoanEquipment(value)}
            loading={isFetchingEquipmentsLoading}
          />
        }

      </Row>

      <Row>
        <Col
          xl={6}
          lg={6}
          md={6}
          sm={12}
          xs={12}
          style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
        >
          <ButtonComponent
            text={intl.formatMessage({ id: "Common.Buttons.Search" })}
            onClick={onButtonClick}
            style={{ minWidth: "100px" }}
            disabled={isFetchingEquipmentsLoading}
          />
          <ButtonComponent
            text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
            onClick={onRemoveFiltersClick}
            style={{ minWidth: "113px" }}
            disabled={isFetchingEquipmentsLoading}
          />
        </Col>
      </Row>

      <div className="pageComponent" style={{ width: "100%" }}>
        <TableComponent
          goToPage={goToPage}
          loading={isFetchingEquipmentsLoading}
          pageCount={pageCount}
          columns={columns}
          manualPagination={true}
          fetchData={fetchData}
          pageSizes={pageSizes}
          initialPage={page}
          initialPageSize={pageSize}
          initialSortBy={initialSortBy}
          data={data}
          dontOrderFirstCollumn={true}
        />
      </div>
    </>
  );
}
